import React, { useState, useEffect, useRef } from 'react';
import { socketService } from '../utils/socket';
import { mediaStorageService } from '../utils/mediaStorage';
import SocketNotification from '../components/SocketNotification';
import './CarouselPage.css';

interface MediaItem {
  id: string;
  type: 'image' | 'video';
  url: string;
  duration?: number;
  name?: string;
  size?: number;
  uploaded?: boolean;
}

const CarouselPage: React.FC = () => {
  const [config, setConfig] = useState({
    autoPlay: true,
    interval: 5000,
    mode: 'sequential' as 'sequential' | 'random',
    transition: 'fade' as 'fade' | 'slide' | 'zoom',
    showControls: true,
    media: [] as MediaItem[]
  });
  
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Refs con el estado vivo para que los handlers registrados en el mount
  // (socket, storage, focus) lean valores frescos y no los del primer render.
  const configRef = useRef(config);
  const currentIndexRef = useRef(currentIndex);
  const isVideoPlayingRef = useRef(isVideoPlaying);
  useEffect(() => { configRef.current = config; }, [config]);
  useEffect(() => { currentIndexRef.current = currentIndex; }, [currentIndex]);
  useEffect(() => { isVideoPlayingRef.current = isVideoPlaying; }, [isVideoPlaying]);
  useEffect(() => {    // Cargar configuración inicial desde localStorage
    const loadConfigFromStorage = () => {
      const savedConfig = localStorage.getItem('neurolimpiadas_app_config');
      if (savedConfig) {
        try {
          const parsed = JSON.parse(savedConfig);
          if (parsed.carousel) {
            console.log('📸 CarouselPage - Configuración cargada:', parsed.carousel);
            setConfig({
              autoPlay: parsed.carousel.autoPlay !== undefined ? parsed.carousel.autoPlay : true,
              interval: parsed.carousel.interval || 5000,
              mode: parsed.carousel.mode || 'sequential',
              transition: parsed.carousel.transition || 'fade',
              showControls: parsed.carousel.showControls !== false,
              media: parsed.carousel.media || []
            });
          }
        } catch (error) {
          console.error('Error loading carousel config:', error);
        }
      }
    };

    // Función para cargar medios desde IndexedDB y sincronizar
    const loadMediaFromIndexedDB = async () => {
      try {
        const restoredMedia = await mediaStorageService.restoreMediaURLs();
        if (restoredMedia.length > 0) {
          console.log(`📸 CarouselPage - ${restoredMedia.length} archivos restaurados desde IndexedDB`);
          
          // Convertir a formato del carousel
          const carouselMedia = restoredMedia.map(item => ({
            id: item.id,
            type: item.type,
            url: item.url,
            duration: item.duration,
            name: item.name,
            size: item.size,
            uploaded: item.uploaded
          }));
          
          // Actualizar configuración con medios restaurados
          setConfig(prev => ({
            ...prev,
            media: carouselMedia
          }));
          
          // Actualizar también en localStorage para mantener sincronización
          const currentConfig = JSON.parse(localStorage.getItem('neurolimpiadas_app_config') || '{}');
          const updatedConfig = {
            ...currentConfig,
            carousel: {
              ...currentConfig.carousel,
              media: carouselMedia
            }
          };
          localStorage.setItem('neurolimpiadas_app_config', JSON.stringify(updatedConfig));
        }
      } catch (error) {
        console.error('Error al cargar medios desde IndexedDB:', error);
      }
    };

    // Cargar configuración inicial
    loadConfigFromStorage();

    // Cargar medios desde IndexedDB
    loadMediaFromIndexedDB();

    // Listener para cambios en localStorage (sincronización entre ventanas)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'neurolimpiadas_app_config' && e.newValue) {
        try {
          const parsed = JSON.parse(e.newValue);
          if (parsed.carousel) {
            console.log('📸 CarouselPage - Configuración sincronizada desde storage:', parsed.carousel);
            setConfig({
              autoPlay: parsed.carousel.autoPlay !== undefined ? parsed.carousel.autoPlay : true,
              interval: parsed.carousel.interval || 5000,
              mode: parsed.carousel.mode || 'sequential',
              transition: parsed.carousel.transition || 'fade',
              showControls: parsed.carousel.showControls !== false,
              media: parsed.carousel.media || []
            });
          }
        } catch (error) {
          console.error('Error parsing storage change:', error);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);    // Listener para cuando la ventana gana el foco (sincronización adicional)
    const handleWindowFocus = () => {
      console.log('📸 CarouselPage - Ventana enfocada, recargando configuración...');
      const previousMediaCount = config.media.length;
      loadConfigFromStorage();
      
      // Pequeño delay para mostrar mensaje si hay cambios
      setTimeout(() => {
        const currentConfig = JSON.parse(localStorage.getItem('neurolimpiadas_app_config') || '{}');
        const newMediaCount = currentConfig.carousel?.media?.length || 0;
        if (newMediaCount !== previousMediaCount) {
          console.log(`🔄 Sincronización completada: ${newMediaCount} archivos multimedia`);
        }
      }, 100);
    };

    window.addEventListener('focus', handleWindowFocus);

    // Escuchar actualizaciones de configuración
    const handleConfigUpdate = (data: any) => {
      if (data.section === 'carousel') {
        console.log('📸 CarouselPage - Configuración actualizada:', data.data);
        setConfig(prev => ({
          ...prev,
          ...data.data,
          media: data.data.media || prev.media
        }));
      }
    };

    // Escuchar comandos del carousel
    const handleCarouselCommand = (data: any) => {
      const { command, payload } = data;
      console.log('📸 CarouselPage - Comando recibido:', command, payload);
      
      try {
        switch (command) {
          case 'play':
            startAutoPlay();
            break;
          case 'pause':
            stopAutoPlay();
            break;
          case 'next':
            nextSlide();
            break;
          case 'previous':
            prevSlide();
            break;
          case 'goTo':
            if (payload && typeof payload.index === 'number') {
              goToSlide(payload.index);
            }
            break;
          case 'reset':
            setCurrentIndex(0);
            break;
          default:
            console.warn('📸 Comando no reconocido:', command);
        }
      } catch (error) {
        console.error('📸 Error ejecutando comando:', command, error);
      }
    };    socketService.on('configUpdate', handleConfigUpdate);
    socketService.on('carouselCommand', handleCarouselCommand);    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('focus', handleWindowFocus);
      socketService.off('configUpdate', handleConfigUpdate);
      socketService.off('carouselCommand', handleCarouselCommand);
    };
    // Handlers referencian funciones que a su vez leen de refs (configRef,
    // currentIndexRef, isVideoPlayingRef). Registramos una sola vez al montar.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Reset index if media array changes
    if (currentIndex >= config.media.length && config.media.length > 0) {
      setCurrentIndex(0);
    }
  }, [config.media, currentIndex]);

  const startAutoPlay = () => {
    if (intervalRef.current) return;
    if (configRef.current.media.length === 0) return;

    intervalRef.current = setInterval(() => {
      if (!isVideoPlayingRef.current) {
        nextSlide();
      }
    }, configRef.current.interval);
    setIsPlaying(true);
  };

  const stopAutoPlay = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setIsPlaying(false);
  };

  const nextSlide = () => {
    const cfg = configRef.current;
    const idx = currentIndexRef.current;
    if (cfg.media.length === 0) return;

    if (cfg.mode === 'random') {
      let newIndex;
      do {
        newIndex = Math.floor(Math.random() * cfg.media.length);
      } while (newIndex === idx && cfg.media.length > 1);
      setCurrentIndex(newIndex);
    } else {
      setCurrentIndex(prev => (prev + 1) % configRef.current.media.length);
    }
  };

  const prevSlide = () => {
    const cfg = configRef.current;
    const idx = currentIndexRef.current;
    if (cfg.media.length === 0) return;

    if (cfg.mode === 'random') {
      let newIndex;
      do {
        newIndex = Math.floor(Math.random() * cfg.media.length);
      } while (newIndex === idx && cfg.media.length > 1);
      setCurrentIndex(newIndex);
    } else {
      setCurrentIndex(prev => (prev - 1 + cfg.media.length) % cfg.media.length);
    }
  };

  const goToSlide = (index: number) => {
    if (index >= 0 && index < configRef.current.media.length) {
      setCurrentIndex(index);
    }
  };

  // Solo arrancamos/paramos el autoplay cuando cambian los parámetros relevantes.
  // startAutoPlay/stopAutoPlay leen de refs, por lo que no hace falta incluirlos
  // en deps; eslint no lo detecta y hay que silenciar la regla.
  useEffect(() => {
    if (config.autoPlay && config.media.length > 0) {
      startAutoPlay();
    } else {
      stopAutoPlay();
    }
    return () => stopAutoPlay();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.autoPlay, config.media.length, config.interval, config.mode]);

  const handleVideoPlay = () => {
    setIsVideoPlaying(true);
    stopAutoPlay();
  };

  const handleVideoEnded = () => {
    setIsVideoPlaying(false);
    nextSlide();
    if (config.autoPlay) {
      setTimeout(() => startAutoPlay(), 100);
    }
  };

  const currentMedia = config.media[currentIndex];
  
  if (config.media.length === 0) {
    return (
      <div className="carousel-page">
        <SocketNotification showOnMount={true} />        <div className="carousel-container">
          <div className="empty-carousel">
            <div className="empty-icon">📸</div>
            <h2>Carousel</h2>
            <p>No hay medios configurados para mostrar.</p>
          </div>
        </div>
        
      </div>
    );
  }

  return (
    <div className="carousel-page">
      <SocketNotification showOnMount={true} />
      <div className="carousel-container">
        <div className="media-container">
          {currentMedia.type === 'image' ? (
            <img
              src={currentMedia.url}
              alt={`Slide ${currentIndex + 1}`}
              className="carousel-image"
              loading="lazy"
              onError={(e) => {
                console.error('Error cargando imagen:', currentMedia.url);
                e.currentTarget.style.display = 'none';
              }}
            />
          ) : (
            <video
              ref={videoRef}
              src={currentMedia.url}
              className="carousel-video"
              autoPlay
              muted
              onPlay={handleVideoPlay}
              onEnded={handleVideoEnded}
              onError={(e) => {
                console.error('Error cargando video:', currentMedia.url);
              }}
              playsInline
            />
          )}
        </div>        {isPlaying && currentMedia.type === 'image' && (
          <div className="progress-container">
            <div
              className="progress-bar"
              style={{
                animationDuration: `${currentMedia.duration || config.interval}ms`
              }}
            />
          </div>
        )}
      </div>
      
    </div>
  );
};

export default CarouselPage;
